import { isDesktop } from "react-device-detect";
import "../../../css/GlobalStyles.css";
import { VerificationAccountType } from "../../../screens/Subscription/SubscriptionPhoneOnboarding";
import { Colors } from "../../../utils/colors";
import RectangleButton from "../../Buttons/RectangleButton";
import { useCallback, useMemo, useState } from "react";
import CustomTextField from "../../CustomTextField";
import CustomPhoneTextField from "../../TextFields/CustomPhoneTextField";
import { showPhoneInputError } from "../../../utils/phoneUtils";
import PlacesAutocomplete, {
  geocodeByAddress,
} from "react-places-autocomplete";
import { AnimatePresence, domAnimation, LazyMotion, m } from "framer-motion";
import { useTheme } from "../../../hooks/useTheme";
import {
  NotificationType,
  TwilioBusinessType,
  VerificationInfo,
} from "@markit/common.types";
import {
  changeFullName,
  getAccountState,
} from "../../../redux/slices/accountSlice";
import { API } from "../../../API";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import { NavigationId } from "../../../navigation/AppParamList";
import { OnboardingStage } from "../../../screens/Login/LoginOnboarding";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { DropdownMenu, DropdownMenuItem } from "../../DropdownMenu";
import DropdownArrowIcon from "../../DropdownsAndTabs/DropdownArrowIcon";
import { hasSubscription, normalizeUrlWithHttps } from "@markit/common.utils";
import { isValid as isValidPublicSuffixHostname } from "psl";

type PhoneOnboardingEnterInfoProps = {
  selectedAccountType: VerificationAccountType;
  onboardingInfo: VerificationInfo;
  setOnboardingInfo: (info: VerificationInfo) => void;
  phoneNumber: string;
  setPhoneNumber: (phoneNumber: string) => void;
};

const PhoneOnboardingEnterInfo = (props: PhoneOnboardingEnterInfoProps) => {
  const {
    selectedAccountType,
    onboardingInfo,
    setOnboardingInfo,
    phoneNumber,
    setPhoneNumber,
  } = props;
  const { theme } = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const accountData = useSelector(getAccountState).account.accountData;
  const [showError, setShowError] = useState(false);
  const [showEINError, setShowEINError] = useState(false);
  const [showWebsiteError, setShowWebsiteError] = useState(false);
  const [phoneNumberError, setPhoneNumberError] = useState({
    isError: false,
    message: "",
  });
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);

  const isBusiness = useMemo(
    () => selectedAccountType === VerificationAccountType.BUSINESS,
    [selectedAccountType]
  );

  const invalidStateCode = useMemo(
    () =>
      onboardingInfo.businessStateProvinceRegion !== "" &&
      onboardingInfo.businessStateProvinceRegion.length > 2,
    [onboardingInfo.businessStateProvinceRegion]
  );

  const setOnboardingField = useCallback(
    (field: keyof VerificationInfo, value: string) => {
      setOnboardingInfo({ ...onboardingInfo, [field]: value });
      setShowError(false);
    },
    [onboardingInfo, setOnboardingInfo]
  );

  const submitVerificationOnPress = useCallback(
    async (businessWebsite: string) => {
      setSubmitting(true);
      try {
        const finalOnboardingInfo: VerificationInfo = {
          ...onboardingInfo,
          businessContactPhone: phoneNumber,
          businessWebsite,
        };
        await API.user.createPhoneVerificationRequest({
          uid: accountData.uid,
          verificationInfo: finalOnboardingInfo,
        });

        const trimmedFullName = onboardingInfo.businessName.trim();
        const fullNameChanged = accountData.fullName !== trimmedFullName;
        if (fullNameChanged && hasSubscription(accountData)) {
          await dispatch(changeFullName(accountData.uid, trimmedFullName));
          await API.user
            .regenerateContactCard({ uid: accountData.uid })
            .catch((e) => console.log(e.message));
        }
        showNotificationBanner(
          dispatch,
          "Submitted Info",
          NotificationType.AFFIRMATIVE
        );
        if (isDesktop) {
          navigate(NavigationId.HOME_ACCOUNT_STATUS);
        } else {
          // This verification onboarding flow is only accessible via sign up onboarding on mobile flow
          navigate(NavigationId.ONBOARDING, {
            state: { defaultStage: OnboardingStage.FINISH_SETUP },
          });
        }
      } catch (e: any) {
        alert(
          "An error occurred while submitting your info. Please try again."
        );
        console.error(e);
      } finally {
        setSubmitting(false);
      }
    },
    [accountData, dispatch, navigate, onboardingInfo, phoneNumber]
  );

  const verifyInfoOnPress = useCallback(async () => {
    let hasError = false;

    // Check for empty fields (except for businessContactPhone and businessRegistrationNumber)
    if (
      Object.entries(onboardingInfo).some(
        ([key, value]) =>
          key !== "businessContactPhone" &&
          key !== "businessRegistrationNumber" &&
          !value
      )
    ) {
      setShowError(true);
      hasError = true;
    }

    // Check EIN number (if business) for length and only numbers
    const cleanedEIN = onboardingInfo.businessRegistrationNumber.replace(
      /\D/g,
      ""
    ); // remove hyphens
    if (
      isBusiness &&
      (cleanedEIN.length !== 9 || !/^[0-9]+$/.test(cleanedEIN))
    ) {
      setShowEINError(true);
      hasError = true;
    }

    // Check phone number
    if (showPhoneInputError(phoneNumber, setPhoneNumberError)) {
      hasError = true;
    }

    // Check invalid website url, auto-prepending https:// to bare domains.
    const normalizedWebsite = normalizeUrlWithHttps(
      onboardingInfo.businessWebsite
    );
    let isValidUrl = false;
    try {
      isValidUrl = isValidPublicSuffixHostname(
        new URL(normalizedWebsite).hostname
      );
    } catch {
      isValidUrl = false;
    }
    if (!isValidUrl) {
      setShowWebsiteError(true);
      hasError = true;
    }

    // Check state code
    if (invalidStateCode) {
      setShowError(true);
      hasError = true;
    }

    if (!hasError) {
      await submitVerificationOnPress(normalizedWebsite);
    }
  }, [
    invalidStateCode,
    isBusiness,
    onboardingInfo,
    phoneNumber,
    submitVerificationOnPress,
  ]);

  const handleSelect = async (value: string) => {
    const results = await geocodeByAddress(value);
    const streetAddress = results[0].formatted_address.split(",")[0];
    const addressComponents = results[0].address_components;

    const localAddress = {
      city: "",
      state: "",
      zip: "",
    };
    const city = addressComponents.find((c) =>
      c.types.includes("locality")
    )?.long_name;
    if (city) {
      localAddress.city = city;
    }
    const state = addressComponents.find((c) =>
      c.types.includes("administrative_area_level_1")
    )?.short_name;
    if (state) {
      localAddress.state = state;
    }
    const zip = addressComponents.find((c) =>
      c.types.includes("postal_code")
    )?.long_name;
    if (zip) {
      localAddress.zip = zip;
    }

    setOnboardingInfo({
      ...onboardingInfo,
      businessStreetAddress: streetAddress,
      businessCity: localAddress.city,
      businessStateProvinceRegion: localAddress.state,
      businessPostalCode: localAddress.zip,
    });
    setIsSearchModalOpen(false);
  };

  const renderInputField = useCallback(
    (
      label: string,
      placeholder: string,
      value: string,
      setValue: (value: string) => void,
      footerText?: string,
      isError?: boolean
    ) => {
      return (
        <div className="ColumnNormal" style={{ gap: 7, width: "100%" }}>
          <span className="bodySmallMedium">{label}</span>
          <CustomTextField
            value={value}
            placeholder={placeholder}
            inputMode="text"
            borderRadius={12}
            onChange={(name: any) => {
              setValue(name.target.value);
            }}
            maxLength={60}
            error={(value === "" && showError) || isError}
            width="100%"
          />
          {footerText ? (
            <span
              style={{
                fontSize: 12,
                color: isError ? Colors.RED1 : Colors.GRAY2,
              }}
            >
              {footerText}
            </span>
          ) : null}
        </div>
      );
    },
    [showError]
  );

  const dropdownItems: DropdownMenuItem[] = useMemo(
    () =>
      Object.values(TwilioBusinessType)
        .filter((type) => type !== TwilioBusinessType.SOLE_PROPRIETOR)
        .map((type) => ({
          title: type,
          icon:
            type === onboardingInfo.businessType
              ? "ion:checkmark-circle"
              : undefined,
          key: type,
          onPress: () =>
            setOnboardingInfo({ ...onboardingInfo, businessType: type }),
        })),
    [onboardingInfo, setOnboardingInfo]
  );

  return (
    <>
      <div
        className={isDesktop ? "AlignedColumn" : "ColumnNormal"}
        style={{ gap: 14, width: isDesktop ? 330 : undefined }}
      >
        {renderInputField(
          isBusiness ? "Legal Business Name" : "Account Name",
          isBusiness ? "Legal Business Name" : "Account Name",
          onboardingInfo.businessName,
          (value: string) => setOnboardingField("businessName", value),
          isBusiness
            ? "*Must match your IRS documents (CP 575 or 147C) exactly. Your account name will be updated to match this. You can change it after approval."
            : "*You will not be able to change this until after approval."
        )}
        {isBusiness ? (
          <>
            {renderInputField(
              "Employer Identification Number (EIN)",
              "12-3456789",
              onboardingInfo.businessRegistrationNumber,
              (value: string) => {
                setOnboardingField("businessRegistrationNumber", value);
                setShowEINError(false);
              },
              showEINError ? "Invalid EIN number" : undefined,
              showEINError
            )}
            <div className="ColumnNormal" style={{ gap: 7, width: "100%" }}>
              <span className="bodySmallMedium">Business Type</span>
              <DropdownMenu
                dropdownItems={dropdownItems}
                trigger={["click"]}
                placement="bottom"
                onOpenChange={(isOpen: boolean) => setDropdownVisible(isOpen)}
              >
                <div
                  className="AlignedRowSpaced"
                  style={{
                    padding: "8px 14px",
                    borderRadius: 14,
                    border: `1px solid ${Colors.GRAY2}`,
                  }}
                >
                  <div className="ColumnNormal" style={{ gap: 4 }}>
                    <span className="bodyRegular">
                      {onboardingInfo.businessType}
                    </span>
                  </div>
                  <DropdownArrowIcon isOpen={dropdownVisible} height={16} />
                </div>
              </DropdownMenu>
            </div>
          </>
        ) : null}
        {renderInputField(
          isBusiness ? "Account Holder First Name" : "First Name",
          "First Name",
          onboardingInfo.businessContactFirstName,
          (value: string) =>
            setOnboardingField("businessContactFirstName", value)
        )}
        {renderInputField(
          isBusiness ? "Account Holder Last Name" : "Last Name",
          "Last Name",
          onboardingInfo.businessContactLastName,
          (value: string) =>
            setOnboardingField("businessContactLastName", value)
        )}
        {renderInputField(
          isBusiness ? "Business Email" : "Email",
          "Email",
          onboardingInfo.businessContactEmail,
          (value: string) => setOnboardingField("businessContactEmail", value)
        )}
        <div className="ColumnNormal" style={{ gap: 7, width: "100%" }}>
          <span className="bodySmallMedium">
            {isBusiness ? "Business Phone Number" : "Phone number"}
          </span>
          <CustomPhoneTextField
            placeholder="Phone number"
            phoneNumber={phoneNumber}
            onPhoneChange={(value: string) => {
              setPhoneNumberError({
                isError: false,
                message: "",
              });
              setPhoneNumber(value || "");
            }}
            showPhoneError={phoneNumberError}
            autoCompleteOn
          />
        </div>
        {renderInputField(
          isBusiness ? "Business Website" : "Personal Website",
          "Website",
          onboardingInfo.businessWebsite,
          (value: string) => {
            setOnboardingField("businessWebsite", value);
            setShowWebsiteError(false);
          },
          showWebsiteError
            ? "Enter a valid website url."
            : `Only for phone carrier review to verify your ${
                isBusiness ? "business" : "identity"
              }. This does not appear on your Markit account.`,
          showWebsiteError
        )}
        <div className="ColumnNormal" style={{ gap: 7, width: "100%" }}>
          <span className="bodySmallMedium">
            {isBusiness ? "Business Address" : "Address"}
          </span>
          <PlacesAutocomplete
            value={onboardingInfo.businessStreetAddress}
            onChange={(value) => {
              setOnboardingField("businessStreetAddress", value);
            }}
            onSelect={handleSelect}
          >
            {({
              getInputProps,
              suggestions,
              getSuggestionItemProps,
              loading,
            }) => (
              <div style={{ width: "100%", position: "relative" }}>
                <input
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "10px 14px",
                    fontSize: 14,
                    borderRadius: 12,
                    border: "1px solid",
                    borderColor:
                      onboardingInfo.businessStreetAddress === "" && showError
                        ? theme.ErrorBorderColor.borderColor
                        : isSearchModalOpen
                          ? theme.FocusedTextFieldBorder.borderColor
                          : theme.EditableTextFieldBorder.borderColor,
                  }}
                  {...getInputProps({
                    placeholder: "Address",
                    className: "location-search-input",
                  })}
                  onClick={() => setIsSearchModalOpen(true)}
                  onFocus={() => setIsSearchModalOpen(true)}
                  onBlur={() => setIsSearchModalOpen(false)}
                />
                {isSearchModalOpen &&
                onboardingInfo.businessStreetAddress !== "" ? (
                  <AnimatePresence>
                    <LazyMotion features={domAnimation}>
                      <m.div
                        className="HideScrollbar autocomplete-dropdown-container"
                        initial={{ y: -10, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -10, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        style={{
                          zIndex: 1000,
                          left: 0,
                          right: 0,
                          position: "absolute",
                          marginTop: 4,
                          border: `1px solid ${theme.DividerColor.borderColor}`,
                          borderRadius: 14,
                          padding: 4,
                          ...theme.SecondaryBG,
                        }}
                      >
                        {loading ? (
                          <div style={{ padding: 10 }}>
                            <span className="bodySubtext">Loading...</span>
                          </div>
                        ) : suggestions.length > 0 ? (
                          suggestions.map((suggestion) => {
                            const className = suggestion.active
                              ? "suggestion-item--active"
                              : "suggestion-item";

                            return (
                              <div
                                {...getSuggestionItemProps(suggestion, {
                                  className,
                                })}
                                className="ColumnNormalSelect LocationItemLight"
                                style={{ gap: 4, padding: 10 }}
                                key={suggestion.id}
                              >
                                <span className="bodyMedium">
                                  {suggestion.formattedSuggestion.mainText}
                                </span>
                                <span className="smallBodySubtext">
                                  {suggestion.formattedSuggestion.secondaryText}
                                </span>
                              </div>
                            );
                          })
                        ) : (
                          <div style={{ padding: 10 }}>
                            <span className="bodySubtext">
                              No Results Found
                            </span>
                          </div>
                        )}
                      </m.div>
                    </LazyMotion>
                  </AnimatePresence>
                ) : null}
              </div>
            )}
          </PlacesAutocomplete>
        </div>
        {renderInputField(
          "City",
          "City",
          onboardingInfo.businessCity,
          (value: string) => setOnboardingField("businessCity", value)
        )}
        <div
          className="AlignedRow"
          style={{ gap: 14, alignItems: "flex-start" }}
        >
          {renderInputField(
            "State",
            "State",
            onboardingInfo.businessStateProvinceRegion,
            (value: string) =>
              setOnboardingField("businessStateProvinceRegion", value),
            invalidStateCode
              ? "Enter based on the two-letter postal code"
              : undefined,
            invalidStateCode
          )}
          {renderInputField(
            "Postal Code",
            "12345",
            onboardingInfo.businessPostalCode,
            (value: string) => setOnboardingField("businessPostalCode", value)
          )}
        </div>
      </div>
      <div
        className="ColumnNormal"
        style={{ width: isDesktop ? 330 : undefined, gap: 10 }}
      >
        <span className="smallBodySubtext" style={{ textAlign: "center" }}>
          Before continuing, double-check all of your info is correct.
        </span>
        <RectangleButton
          buttonLabel={"Submit and Continue"}
          containerStyles={{ paddingInline: 10, paddingBlock: 14 }}
          altTextColor={Colors.WHITE}
          altColor={Colors.BLACK}
          onPress={verifyInfoOnPress}
          loading={submitting}
        />
      </div>
    </>
  );
};

export default PhoneOnboardingEnterInfo;
